import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Navbar from "../home/components/Navbar";
import FooterSection from "../home/components/FooterSection";
import { useUser } from "../../contexts/UserContext";

export default function RegisterPage() {
  const { register } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: string })?.from ?? "/profile";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const inputCls =
    "w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-emerald-500 transition-colors bg-white placeholder:text-stone-400";

  const validate = (): string | null => {
    if (!name.trim() || name.trim().length < 2) return "Adınız en az 2 karakter olmalıdır.";
    if (name.trim().length > 50) return "Adınız en fazla 50 karakter olabilir.";
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
      return "Geçerli bir e-posta adresi girin.";
    if (!password) return "Şifre boş olamaz.";
    if (password.length < 6) return "Şifre en az 6 karakter olmalıdır.";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const ve = validate();
    if (ve) { setError(ve); return; }

    setError("");
    setInfo("");
    setSubmitting(true);

    const res = await register(name, email, password);
    setSubmitting(false);

    if (!res.success) {
      if (res.error?.includes("Hesabınız oluşturuldu") || res.error?.includes("doğrulama")) {
        setInfo(res.error);
      } else {
        setError(res.error ?? "Kayıt oluşturulamadı.");
      }
    } else {
      navigate(from, { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="min-h-[calc(100vh-200px)] bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center px-4 pt-20 pb-12">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex w-16 h-16 rounded-2xl bg-emerald-700 items-center justify-center mb-4">
              <i className="ri-map-2-line text-white text-2xl" />
            </div>
            <h1 className="font-bold text-stone-900 text-2xl mb-1">BursaGuide360</h1>
            <p className="text-stone-500 text-sm">Ücretsiz hesap oluşturun</p>
          </div>

          {/* Card */}
          <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
            {/* Tab Switch */}
            <div className="flex border-b border-stone-100">
              <Link
                to="/login"
                state={{ from }}
                className="flex-1 py-3.5 text-sm font-semibold text-stone-500 hover:text-stone-700 hover:bg-stone-50 whitespace-nowrap text-center transition-colors"
              >
                Giriş Yap
              </Link>
              <button
                type="button"
                className="flex-1 py-3.5 text-sm font-semibold text-emerald-700 border-b-2 border-emerald-600 bg-emerald-50/50 whitespace-nowrap"
              >
                Üye Ol
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-7 space-y-4" noValidate>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">
                  Adınız{" "}
                  <span className="text-stone-400 font-normal text-xs">(görünen ad)</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ahmet Yılmaz"
                  autoComplete="name"
                  disabled={submitting}
                  className={inputCls}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">
                  E-posta
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ornek@eposta.com"
                  autoComplete="new-email"
                  disabled={submitting}
                  className={inputCls}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-stone-700">Şifre</label>
                  <span className="text-xs text-stone-400">En az 6 karakter</span>
                </div>
                <div className="relative">
                  <input
                    type={showPass ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    disabled={submitting}
                    className={`${inputCls} pr-11`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass((p) => !p)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 cursor-pointer w-5 h-5 flex items-center justify-center"
                  >
                    <i className={showPass ? "ri-eye-off-line" : "ri-eye-line"} />
                  </button>
                </div>
              </div>

              {/* Error Banner */}
              {error && (
                <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  <i className="ri-error-warning-line text-red-500 text-sm mt-0.5 flex-shrink-0" />
                  <p className="text-red-600 text-sm leading-relaxed">{error}</p>
                </div>
              )}

              {/* Info Banner */}
              {info && (
                <div className="flex items-start gap-2.5 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                  <i className="ri-information-line text-emerald-600 text-sm mt-0.5 flex-shrink-0" />
                  <p className="text-emerald-700 text-sm leading-relaxed">{info}</p>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-xl font-semibold cursor-pointer whitespace-nowrap transition-colors text-sm"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />
                    Hesap oluşturuluyor...
                  </span>
                ) : (
                  "Hesap Oluştur"
                )}
              </button>

              <p className="text-center text-xs text-stone-400 leading-relaxed">
                Hesap oluşturarak{" "}
                <span className="text-stone-600">kullanım koşullarını</span> kabul etmiş
                olursunuz.
              </p>
            </form>
          </div>

          {/* Switch mode */}
          <p className="text-center text-sm text-stone-500 mt-5">
            Zaten üye misiniz?{" "}
            <Link
              to="/login"
              state={{ from }}
              className="text-emerald-700 font-semibold cursor-pointer hover:underline whitespace-nowrap"
            >
              Giriş yap
            </Link>
          </p>
        </div>
      </div>

      <FooterSection />
    </div>
  );
}